import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, postForFile } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useInvalidateIdentity, useKeys } from "@/lib/queries";
import type { MyVoice } from "@/lib/types";

/**
 * core-api's routes for a person's own voice (ADR-117 in platform). Every one is the person's own
 * but the last: an admin deletes a person's voice, and never makes one.
 */

/** The voice that speaks for me, and what a recording has to be. */
export function useMyVoice(enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["my-voice"]),
    queryFn: () => api.get<MyVoice>("/auth/me/voice"),
    enabled,
  });
}

/** After my voice changed: this page's answer, my account, and the lists that show it. */
function useVoiceChanged() {
  const keys = useKeys();
  const qc = useQueryClient();
  const invalidate = useInvalidateIdentity();
  const { reload } = useAuth();
  return (mine: MyVoice) => {
    qc.setQueryData(keys.scoped(["my-voice"]), mine);
    invalidate();
    void reload().catch(() => undefined);
  };
}

/**
 * Make my own voice from a recording of me, with my yes. core-api sends the recording to
 * ElevenLabs before it answers, so this takes several seconds.
 */
export function useRecordOwnVoice() {
  const changed = useVoiceChanged();
  return useMutation({
    mutationFn: ({ wav, agreed }: { wav: Blob; agreed: boolean }) => {
      const form = new FormData();
      form.append("file", wav, "my-voice.wav");
      form.append("agreed", agreed ? "true" : "false");
      return api.put<MyVoice>("/auth/me/voice/sample", form);
    },
    onSuccess: changed,
  });
}

/** Delete my own voice, here and at ElevenLabs. */
export function useDeleteOwnVoice() {
  const changed = useVoiceChanged();
  return useMutation({
    mutationFn: () => api.delete<MyVoice>("/auth/me/voice/sample"),
    onSuccess: changed,
  });
}

/** A sentence in my own voice, as sound to play. With no text it says one with my name. */
export async function hearOwnVoice(input: { text?: string; language?: string } = {}): Promise<Blob> {
  return (await postForFile("/auth/me/voice/hear", input)).blob;
}

/** An admin deletes a person's own voice. Their greetings go back to the team's voice. */
export function useDeleteUserVoice() {
  const invalidate = useInvalidateIdentity();
  return useMutation({
    mutationFn: (userId: number) => api.delete<void>(`/app-users/${userId}/voice`),
    onSuccess: () => invalidate(),
  });
}
